  import Checkbox from "../../../../components/Checkbox/Checkbox"
  import AnalysisSection from "../AnalysisSection/AnalysisSection"
  import { AnalysisDecisionSectionProps } from "./AnalysisDecisionSection.types"
  import "./AnalysisDecisionSection.css"
  import { useEffect, useState } from "react"
import EllipsisMenu from "../../../../components/EllipsisMenu/EllipsisMenu"
import Button from "../../../../components/Button/Button"
import { MAX_WORKFLOW_OVERVIEW_COMMENT_CHARACTERS } from "../../../../utils/constants"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import ErrorView from "../SectionError/ErrorView"

  const AnalysisDecisionSection = (props: AnalysisDecisionSectionProps) => {
    const {
      workflowAnalysis,
      onEdit,
      onAdd
    } = props

    const [showMenu, setShowMenu] = useState<boolean>(false)
    const [workflowOverview, setWorkflowOverview] = useState<string>("")
    const [workflowOverviewError, setWorkflowOverviewError] = useState<string>("")
    const [cuiSuspected, setCuiSuspected] = useState<boolean>(false)
    const [muiSuspected, setMuiSuspected] = useState<boolean>(false)
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const skeletonLoadingHeight = "6rem"
    
    useEffect(() => {
      if (workflowAnalysis.status !== "success") { return }
      if (workflowAnalysis.data === null) { return }
      if (workflowAnalysis.data.length === 0) { return }
      setWorkflowOverview(workflowAnalysis.data[0].comment)
      setCuiSuspected(workflowAnalysis.data[0].corrosion_suspected)
      setMuiSuspected(workflowAnalysis.data[0].moisture_suspected)
    }, [workflowAnalysis])

    const isValidWorkflowOverview = () => {
      if (workflowOverview === "") {
        setWorkflowOverviewError("Cannot save empty workflow overview.")
        return false
      }

      if (workflowOverview.length > MAX_WORKFLOW_OVERVIEW_COMMENT_CHARACTERS) {
        setWorkflowOverviewError(`Workflow overview exceeded the max number of characters (${workflowOverview.length}/${MAX_WORKFLOW_OVERVIEW_COMMENT_CHARACTERS})`)
        return false
      }
      return true
    }

    const onSaveEdits = () => {
      if (workflowAnalysis.status !== "success") { 
        setIsEditing(false)
        return
      }
      if (!isValidWorkflowOverview()) { return }
      
      if (workflowAnalysis.data === null || workflowAnalysis.data.length === 0) {
        onAdd(workflowOverview, cuiSuspected, muiSuspected)
      } else {
        onEdit(workflowAnalysis.data[0].id ?? 0, workflowOverview, cuiSuspected, muiSuspected)
      }

      setIsEditing(false)
    }

    const onCancel = () => {
      setIsEditing(false)
      setWorkflowOverviewError("")

      if (workflowAnalysis.status !== "success") { return }
      if (workflowAnalysis.data === null || workflowAnalysis.data.length === 0) { 
        setWorkflowOverview("")
        return
      }
      setWorkflowOverview(workflowAnalysis.data[0].comment)
    }

    if (workflowAnalysis.status === "loading") {
      return (
        <AnalysisSection title="Workflow Overview">
          <div className="analysis-section">
            <SkeletonLoadingView style={{height: skeletonLoadingHeight}} />
          </div>
        </AnalysisSection>
      )
    }

    if (workflowAnalysis.status === "error") {
      return (
        <AnalysisSection title="Workflow Overview">
        <ErrorView 
          message={"Failed to load workflow overview."}
        />
      </AnalysisSection>
      )
    }

    return (
      <AnalysisSection
        title="Workflow Overview"
        titleChild={
          <EllipsisMenu 
            ellipsisMenu={{
              items: [{
                item: "Edit",
                onClick: () => setIsEditing(true)
              }],
              showMenu: showMenu,
              onClick: () => setShowMenu(true),
              onHide: () => setShowMenu(false),
            }}
          />
        }
      >
        <div className="analysis-section analysis-decision-container">
          <textarea
            disabled={!isEditing}
            className="analysis-comment-text"
            value={workflowOverview}
            onChange={(e) => setWorkflowOverview(e.target.value)}
            placeholder="No comment saved."
          />
          {workflowOverviewError !== "" && (
            <p className="workflow-overview-error">{workflowOverviewError}</p>
          )}

          <div className="analysis-decision-flex-container">
            <Checkbox
              label={"Suspected Corrosion"}
              checkbox={{
                value: cuiSuspected,
                onChange: () => setCuiSuspected(!cuiSuspected)
              }}
              enabled={isEditing}
              isLoading={false}
            />

            <Checkbox
              label={"Suspected Moisture"}
              checkbox={{
                value: muiSuspected,
                onChange: () => setMuiSuspected(!muiSuspected)
              }}
              enabled={isEditing}
              isLoading={false}
            />
          </div>

          {isEditing && (
            <div className="save-analysis-container">
              <Button title="Save" onClick={onSaveEdits} disabled={workflowOverview === ""} />
              <Button title="Cancel" type="outline" onClick={onCancel} />
            </div>
          )}
        </div>
      </AnalysisSection>
    )
  }

  export default AnalysisDecisionSection